/**
 * 設定（docs/01-requirements.md §5.5, §5.14）。
 *
 * カテゴリ管理の摩擦は意図的な設計（§4.3）。
 * 「少数の粗い語彙」を保つための防波堤なので、追加を手軽にしない。
 * 追加フォームは既定で閉じておき、開いたときに方針を一言添える。
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from '@app/api/client';
import { AppHeader } from '@app/components/AppHeader';
import { errorMessage, ErrorNotice } from '@app/components/ErrorNotice';
import { useCategories } from '@app/components/useCategories';
import { Icon } from '@app/components/icons';
import { exportAllAsZip, downloadBlob, type ExportProgress } from '@app/export/export-zip';
import { useToast } from '@app/ui/toast';
import { todayJst } from '@shared/date';
import type { Category } from '@shared/types';

const PHASE_LABEL: Record<ExportProgress['phase'], string> = {
  manifest: '一覧を取得中',
  notes: 'ノートを取得中',
  images: '画像を取得中',
  zip: 'ZIP を作成中',
  done: '完了',
};

export function SettingsPage() {
  const toast = useToast();
  const categories = useCategories();

  return (
    <div className="page">
      <AppHeader title="設定" backTo="/" />
      <main className="page__body settings">
        <CategorySection categories={categories} />
        <ExportSection />
        <AccountSection onError={(message) => toast.show(message)} />
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// カテゴリ
// ---------------------------------------------------------------------------

function CategorySection({ categories }: { categories: ReturnType<typeof useCategories> }) {
  const toast = useToast();
  const [items, setItems] = useState<Category[]>([]);
  const [names, setNames] = useState<Record<number, string>>({});
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setItems(categories.categories);
    setNames(Object.fromEntries(categories.categories.map((c) => [c.id, c.name])));
  }, [categories.categories]);

  const rename = useCallback(
    (category: Category) => {
      const next = (names[category.id] ?? '').trim();
      if (next === '' || next === category.name) return;
      void (async () => {
        setBusy(true);
        try {
          await api.updateCategory(category.id, { name: next });
          toast.show('カテゴリ名を変更しました');
          categories.reload();
        } catch (error: unknown) {
          toast.show(`変更できませんでした: ${errorMessage(error)}`);
        } finally {
          setBusy(false);
        }
      })();
    },
    [categories, names, toast],
  );

  const move = useCallback(
    (index: number, direction: -1 | 1) => {
      const target = index + direction;
      if (target < 0 || target >= items.length) return;
      const reordered = [...items];
      const a = reordered[index];
      const b = reordered[target];
      if (!a || !b) return;
      reordered[index] = b;
      reordered[target] = a;
      setItems(reordered);
      void (async () => {
        setBusy(true);
        try {
          // 並び順は 1 リクエストで一括更新する。個別更新を並列に投げると、
          // 途中で失敗したときに順序が中途半端な状態で残る。
          await api.reorderCategories(reordered.map((category) => category.id));
          categories.reload();
        } catch (error: unknown) {
          toast.show(`並べ替えできませんでした: ${errorMessage(error)}`);
          categories.reload();
        } finally {
          setBusy(false);
        }
      })();
    },
    [categories, items, toast],
  );

  const add = useCallback(() => {
    const name = newName.trim();
    if (name === '') return;
    void (async () => {
      setBusy(true);
      try {
        await api.createCategory(name);
        setNewName('');
        setAddOpen(false);
        toast.show('カテゴリを追加しました');
        categories.reload();
      } catch (error: unknown) {
        toast.show(`追加できませんでした: ${errorMessage(error)}`);
      } finally {
        setBusy(false);
      }
    })();
  }, [categories, newName, toast]);

  return (
    <section className="al-section settings__section">
      <h2>カテゴリ</h2>
      <p className="muted">
        ノートに付けられるカテゴリは高々 1 つです。日記の断片にはカテゴリを付けない運用が基本です。
      </p>

      <div className="settings__body">
        {categories.error != null && (
          <ErrorNotice error={categories.error} onRetry={categories.reload} />
        )}
        {categories.loading && <p className="muted">読み込み中…</p>}

        <ul className="category-list">
          {items.map((category, index) => (
            <li key={category.id} className="category-row">
              <input
                className="al-input"
                type="text"
                value={names[category.id] ?? ''}
                aria-label={`${category.name} の名前`}
                onChange={(event) =>
                  setNames((prev) => ({ ...prev, [category.id]: event.target.value }))
                }
              />
              <button
                type="button"
                className="al-button al-button--secondary"
                disabled={busy || (names[category.id] ?? '').trim() === category.name}
                onClick={() => rename(category)}
              >
                改名
              </button>
              <button
                type="button"
                className="icon-button"
                aria-label="上へ"
                disabled={busy || index === 0}
                onClick={() => move(index, -1)}
              >
                <Icon name="arrow-up" />
              </button>
              <button
                type="button"
                className="icon-button"
                aria-label="下へ"
                disabled={busy || index === items.length - 1}
                onClick={() => move(index, 1)}
              >
                <Icon name="arrow-down" />
              </button>
            </li>
          ))}
        </ul>

        {items.length === 0 && !categories.loading && (
          <p className="muted">まだカテゴリはありません。</p>
        )}

        {addOpen ? (
          <div className="category-add">
            {/* 追加をためらわせるための案内。責める文言にはしない（DESIGN-SYSTEM.md §9）。 */}
            <p className="al-notice al-notice--lilac">
              カテゴリは「技術」「レシピ」のような、少数の粗い語彙にとどめてください。
              細かく分けるほど、書くときの迷いが増え、後から見返しにくくなります。
            </p>
            <div className="category-row">
              <input
                className="al-input"
                type="text"
                value={newName}
                placeholder="新しいカテゴリ名"
                aria-label="新しいカテゴリ名"
                onChange={(event) => setNewName(event.target.value)}
              />
              <button
                type="button"
                className="al-button al-button--secondary"
                disabled={busy || newName.trim() === ''}
                onClick={add}
              >
                追加する
              </button>
              <button
                type="button"
                className="al-button al-button--secondary"
                onClick={() => setAddOpen(false)}
              >
                やめる
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className="link-quiet" onClick={() => setAddOpen(true)}>
            カテゴリを追加する…
          </button>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// エクスポート
// ---------------------------------------------------------------------------

function ExportSection() {
  const toast = useToast();
  const [progress, setProgress] = useState<ExportProgress | null>(null);
  const [running, setRunning] = useState(false);

  const run = useCallback(() => {
    void (async () => {
      setRunning(true);
      setProgress(null);
      try {
        const blob = await exportAllAsZip((next) => setProgress(next));
        downloadBlob(blob, `mamemo-6-export-${todayJst()}.zip`);
        toast.show('エクスポートが完了しました');
      } catch (error: unknown) {
        toast.show(`エクスポートに失敗しました: ${errorMessage(error)}`);
      } finally {
        setRunning(false);
      }
    })();
  }, [toast]);

  return (
    <section className="al-section settings__section">
      <h2>エクスポート</h2>
      <p className="muted">
        全ノート（Markdown）と画像、メタデータを 1 つの ZIP にまとめます。
        mamemo-6 がなくても本文と画像を読める形式です。件数が多いと数分かかることがあります。
      </p>
      <div className="settings__body">
        <button
          type="button"
          className="al-button al-button--primary"
          disabled={running}
          onClick={run}
        >
          {running ? 'エクスポート中…' : 'エクスポートを実行'}
        </button>
        {progress && (
          <p className="muted" role="status" aria-live="polite">
            {PHASE_LABEL[progress.phase]}
            {progress.total > 1 ? `（${progress.loaded} / ${progress.total}）` : ''}
          </p>
        )}
        {running && <p className="muted">この画面を閉じると中断されます。</p>}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// アカウント
// ---------------------------------------------------------------------------

function AccountSection({ onError }: { onError: (message: string) => void }) {
  const [busy, setBusy] = useState(false);

  const logout = useCallback(() => {
    if (!window.confirm('ログアウトしますか？')) return;
    void (async () => {
      setBusy(true);
      try {
        await api.logout();
        window.location.reload();
      } catch (error: unknown) {
        onError(`ログアウトできませんでした: ${errorMessage(error)}`);
        setBusy(false);
      }
    })();
  }, [onError]);

  return (
    <section className="al-section settings__section">
      <h2>アカウント</h2>
      <div className="settings__body">
        <button
          type="button"
          className="al-button al-button--secondary"
          disabled={busy}
          onClick={logout}
        >
          ログアウト
        </button>
      </div>
    </section>
  );
}
